import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ExtensionIcon from "@mui/icons-material/Extension";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { toPng } from "html-to-image";
import SlidingPlayMain from "../../../admin/story/SlidingPlayMain";
import { markScreenshotNotReady, signalScreenshotSceneReady } from "../../../lib/screenshotReady";
import ScreenshotExpiredPage from "../components/ScreenshotExpiredPage";
import {
    getImageUrl,
    getTemplateGrid,
    getTemplateStartingLayout,
    loadScreenshotSliding,
} from "../lib/screenshotApi";
import { isScreenshotAccessExpired } from "../lib/screenshotAccess";
import { FEATURE_GRAPHIC_INSTAGRAM_PATH } from "../routes";
import { getScreenshotScene, isScreenshotSceneConfigured } from "../screenshotScenes";
import { INSTAGRAM_FEATURE_GRAPHIC_SIZE } from "./FeatureGraphicPage";

const SAVE_BAR_H = 64;
const SIZE = INSTAGRAM_FEATURE_GRAPHIC_SIZE;
const FILE_NAME = "sliding-puzzle-quest-feature-graphic-instagram-1080x1080.png";
const SCENE_SLUG = "05";

const TITLE_STACK =
    '"Segoe UI", "Arial Black", "Helvetica Neue", Impact, sans-serif';
const TAGLINE_STACK =
    'Georgia, "Palatino Linotype", "Book Antiqua", Palatino, "Times New Roman", serif';

const FEATURE_ITEMS = [
    { label: "Daily Challenges", Icon: TimerOutlinedIcon },
    { label: "Play Together", Icon: EmojiEventsIcon },
    { label: "1000+ Puzzles", Icon: ExtensionIcon },
];

/**
 * Instagram 1080×1080 feature graphic — title + promo on top, puzzle center, CTAs bottom.
 * Fills the full square (no letterboxing of the landscape Play graphic).
 */
export default function FeatureGraphicInstagramPage() {
    const scene = getScreenshotScene(SCENE_SLUG);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [group, setGroup] = useState(null);
    const [template, setTemplate] = useState(null);
    const [previewScale, setPreviewScale] = useState(1);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const frameRef = useRef(null);

    const expired = isScreenshotAccessExpired();

    useEffect(() => {
        if (expired) {
            setLoading(false);
            return undefined;
        }
        if (!scene || !isScreenshotSceneConfigured(scene)) {
            setError("Feature graphic scene is not configured");
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await loadScreenshotSliding(scene);
                if (cancelled) return;
                setGroup(result.group);
                setTemplate(result.template);
            } catch (e) {
                if (!cancelled) setError(e?.message || "Failed to load puzzle");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [expired, scene]);

    const imageUrl = useMemo(() => getImageUrl(group?.story), [group]);
    const gridSize = useMemo(() => getTemplateGrid(template), [template]);
    const startingLayout = useMemo(
        () => getTemplateStartingLayout(template),
        [template],
    );

    useEffect(() => {
        if (expired || loading || error || !imageUrl || !startingLayout.length) {
            markScreenshotNotReady();
            return undefined;
        }
        signalScreenshotSceneReady({
            path: FEATURE_GRAPHIC_INSTAGRAM_PATH,
            slug: "feature-graphic-instagram",
        });
        return undefined;
    }, [expired, loading, error, imageUrl, startingLayout.length]);

    useEffect(() => {
        const updateScale = () => {
            const padding = 32;
            const availableW = window.innerWidth - padding;
            const availableH = window.innerHeight - SAVE_BAR_H - padding;
            setPreviewScale(Math.min(1, availableW / SIZE, availableH / SIZE));
        };
        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
    }, []);

    useEffect(() => {
        const prevHtml = document.documentElement.style.overflow;
        const prevBody = document.body.style.overflow;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        return () => {
            document.documentElement.style.overflow = prevHtml;
            document.body.style.overflow = prevBody;
        };
    }, []);

    const handleSave = useCallback(async () => {
        const node = frameRef.current;
        if (!node || saving) return;

        setSaving(true);
        setSaveError(null);
        const previousTransform = node.style.transform;
        node.style.transform = "none";

        try {
            await new Promise((resolve) =>
                requestAnimationFrame(() => requestAnimationFrame(resolve)),
            );
            const dataUrl = await toPng(node, {
                width: SIZE,
                height: SIZE,
                pixelRatio: 1,
                cacheBust: true,
                skipFonts: true,
                style: {
                    transform: "none",
                    transformOrigin: "top left",
                },
            });
            const link = document.createElement("a");
            link.download = FILE_NAME;
            link.href = dataUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            setSaveError(e?.message || "Failed to save image");
        } finally {
            node.style.transform = previousTransform;
            setSaving(false);
        }
    }, [saving]);

    if (expired) {
        return <ScreenshotExpiredPage />;
    }

    const ready = Boolean(imageUrl && gridSize > 0 && startingLayout.length);

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#020617",
                overflow: "hidden",
                zIndex: 1,
                gap: 1.5,
                pb: `${SAVE_BAR_H}px`,
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    width: Math.round(SIZE * previewScale),
                    height: Math.round(SIZE * previewScale),
                    position: "relative",
                    overflow: "hidden",
                    flexShrink: 0,
                }}
            >
                <Box
                    ref={frameRef}
                    data-screenshot-frame="sliding-feature-graphic-instagram"
                    sx={{
                        width: SIZE,
                        height: SIZE,
                        transform: `scale(${previewScale})`,
                        transformOrigin: "top left",
                        position: "relative",
                        overflow: "hidden",
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        px: "56px",
                        pt: "52px",
                        pb: "48px",
                        boxShadow: "0 0 0 1px rgba(148, 163, 184, 0.25)",
                        bgcolor: "#020617",
                    }}
                >
                    {/* Full-bleed puzzle image background */}
                    {imageUrl ? (
                        <Box
                            aria-hidden
                            sx={{
                                position: "absolute",
                                inset: 0,
                                zIndex: 0,
                                backgroundImage: `url("${imageUrl}")`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                                opacity: 0.6,
                            }}
                        />
                    ) : null}
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 1,
                            background: `
                                radial-gradient(ellipse 70% 45% at 50% 18%, rgba(56, 189, 248, 0.12) 0%, transparent 60%),
                                linear-gradient(180deg, rgba(2, 6, 23, 0.55) 0%, rgba(15, 23, 42, 0.35) 42%, rgba(2, 6, 23, 0.6) 100%)
                            `,
                            pointerEvents: "none",
                        }}
                    />

                    {/* Top: title + promo */}
                    <Box
                        sx={{
                            flex: "0 0 auto",
                            width: "100%",
                            textAlign: "center",
                            zIndex: 2,
                        }}
                    >
                        <Typography
                            component="h1"
                            sx={{
                                m: 0,
                                fontFamily: TITLE_STACK,
                                fontWeight: 900,
                                fontSize: 64,
                                lineHeight: 0.95,
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                                background:
                                    "linear-gradient(180deg, #f8fafc 0%, #bae6fd 28%, #38bdf8 55%, #94a3b8 78%, #e2e8f0 100%)",
                                WebkitBackgroundClip: "text",
                                backgroundClip: "text",
                                color: "transparent",
                                filter:
                                    "drop-shadow(0 0 10px rgba(14, 165, 233, 0.95)) drop-shadow(0 0 28px rgba(56, 189, 248, 0.55)) drop-shadow(0 4px 2px rgba(2, 6, 23, 0.85))",
                                userSelect: "none",
                            }}
                        >
                            Sliding
                            <Box component="br" />
                            Puzzle Quest
                        </Typography>
                        <Typography
                            component="p"
                            sx={{
                                m: 0,
                                mt: 2,
                                fontFamily: TAGLINE_STACK,
                                fontStyle: "italic",
                                fontWeight: 500,
                                fontSize: 22,
                                lineHeight: 1.35,
                                letterSpacing: "0.05em",
                                color: "rgba(226, 232, 240, 0.92)",
                                textShadow: "0 1px 8px rgba(2, 6, 23, 0.8)",
                                userSelect: "none",
                            }}
                        >
                            MASTER THE CHAOS. PIECE TOGETHER THE STORY.
                        </Typography>
                    </Box>

                    {/* Middle: puzzle grid */}
                    <Box
                        sx={{
                            flex: "1 1 auto",
                            width: "100%",
                            minHeight: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            py: 3,
                            zIndex: 2,
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={40} sx={{ color: "#38bdf8" }} />
                        ) : error ? (
                            <Typography color="error" align="center">
                                {error}
                            </Typography>
                        ) : ready ? (
                            <Box
                                sx={{
                                    position: "relative",
                                    width: 560,
                                    height: 560,
                                    flexShrink: 0,
                                }}
                            >
                                <Box
                                    aria-hidden
                                    sx={{
                                        position: "absolute",
                                        inset: -36,
                                        borderRadius: 4,
                                        background:
                                            "radial-gradient(circle at 50% 45%, rgba(56,189,248,0.4) 0%, rgba(167,139,250,0.14) 45%, transparent 70%)",
                                        filter: "blur(10px)",
                                        zIndex: 0,
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: -42,
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                        px: 2.5,
                                        py: 0.85,
                                        borderRadius: 999,
                                        bgcolor: "rgba(2, 6, 23, 0.82)",
                                        border: "1px solid rgba(56, 189, 248, 0.65)",
                                        boxShadow:
                                            "0 0 20px rgba(34, 211, 238, 0.45), inset 0 0 12px rgba(56,189,248,0.15)",
                                        zIndex: 5,
                                        pointerEvents: "none",
                                    }}
                                >
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontFamily:
                                                '"JetBrains Mono", "SF Mono", "Consolas", monospace',
                                            fontWeight: 700,
                                            fontSize: 20,
                                            letterSpacing: "0.08em",
                                            color: "#e0f2fe",
                                            textShadow: "0 0 12px rgba(56,189,248,0.8)",
                                            lineHeight: 1,
                                            userSelect: "none",
                                        }}
                                    >
                                        00:34.12
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        position: "relative",
                                        width: "100%",
                                        height: "100%",
                                        borderRadius: "22px",
                                        p: "12px",
                                        background:
                                            "linear-gradient(145deg, rgba(56,189,248,0.55) 0%, rgba(15,23,42,0.95) 40%, rgba(167,139,250,0.4) 100%)",
                                        boxShadow:
                                            "0 0 0 1px rgba(186,230,253,0.35), 0 24px 48px rgba(0,0,0,0.55), 0 0 60px rgba(56,189,248,0.25)",
                                        zIndex: 1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            borderRadius: "14px",
                                            overflow: "hidden",
                                            bgcolor: "#0f172a",
                                            boxShadow: "inset 0 0 0 2px rgba(14,165,233,0.35)",
                                        }}
                                    >
                                        <SlidingPlayMain
                                            imageUrl={imageUrl}
                                            gridSize={gridSize}
                                            startingLayout={startingLayout}
                                            hideCompletedCaption
                                            fillParent
                                            readOnly
                                            playMoveSound={false}
                                            showMatchCelebrations={false}
                                            gridGutterColor="#0ea5e9"
                                            gridBorderColor="#38bdf8"
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <Typography color="text.secondary">Puzzle image missing.</Typography>
                        )}
                    </Box>

                    {/* Bottom: feature icons + PLAY NOW */}
                    <Box
                        sx={{
                            flex: "0 0 auto",
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 3,
                            zIndex: 2,
                        }}
                    >
                        <Stack direction="row" spacing={4} justifyContent="center">
                            {FEATURE_ITEMS.map(({ label, Icon }) => (
                                <Stack
                                    key={label}
                                    alignItems="center"
                                    spacing={1}
                                    sx={{ width: 140 }}
                                >
                                    <Box
                                        sx={{
                                            width: 52,
                                            height: 52,
                                            borderRadius: "14px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background:
                                                "linear-gradient(145deg, rgba(148,163,184,0.45) 0%, rgba(15,23,42,0.95) 45%, rgba(56,189,248,0.35) 100%)",
                                            border: "1px solid rgba(186, 230, 253, 0.55)",
                                            boxShadow:
                                                "0 0 16px rgba(56,189,248,0.35), inset 0 1px 0 rgba(255,255,255,0.25)",
                                        }}
                                    >
                                        <Icon
                                            sx={{
                                                fontSize: 26,
                                                color: "#e0f2fe",
                                                filter: "drop-shadow(0 0 6px rgba(56,189,248,0.9))",
                                            }}
                                        />
                                    </Box>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontFamily: TITLE_STACK,
                                            fontWeight: 700,
                                            fontSize: 14,
                                            lineHeight: 1.2,
                                            letterSpacing: "0.02em",
                                            color: "rgba(226, 232, 240, 0.9)",
                                            textAlign: "center",
                                            textShadow: "0 1px 6px rgba(2,6,23,0.9)",
                                            userSelect: "none",
                                        }}
                                    >
                                        {label}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>

                        <Box
                            sx={{
                                px: 5,
                                py: 1.6,
                                borderRadius: 999,
                                background:
                                    "linear-gradient(180deg, #7dd3fc 0%, #0ea5e9 45%, #0369a1 100%)",
                                border: "1.5px solid rgba(226, 232, 240, 0.75)",
                                boxShadow:
                                    "0 0 22px rgba(14, 165, 233, 0.85), 0 0 4px rgba(125, 211, 252, 0.9), inset 0 1px 0 rgba(255,255,255,0.45)",
                                minWidth: 200,
                                textAlign: "center",
                            }}
                        >
                            <Typography
                                component="span"
                                sx={{
                                    fontFamily: TITLE_STACK,
                                    fontWeight: 800,
                                    fontSize: 20,
                                    letterSpacing: "0.12em",
                                    color: "#f0f9ff",
                                    textShadow: "0 1px 2px rgba(2,6,23,0.45)",
                                    userSelect: "none",
                                }}
                            >
                                PLAY NOW
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: SAVE_BAR_H,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    bgcolor: "rgba(2, 6, 23, 0.92)",
                    borderTop: "1px solid rgba(148, 163, 184, 0.2)",
                    zIndex: 20,
                    px: 2,
                    boxSizing: "border-box",
                }}
            >
                <Button
                    variant="contained"
                    size="medium"
                    startIcon={
                        saving ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : (
                            <SaveAltIcon />
                        )
                    }
                    onClick={handleSave}
                    disabled={!ready || saving || loading}
                    sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        px: 3,
                        bgcolor: "#0ea5e9",
                        "&:hover": { bgcolor: "#0284c7" },
                    }}
                >
                    {saving ? "Saving…" : "Save 1080×1080 PNG"}
                </Button>
                {saveError ? (
                    <Typography variant="caption" color="error" sx={{ lineHeight: 1.2 }}>
                        {saveError}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}
